import { columnLetter, guessRoleFromLabel, type SheetGrid, type SheetRole } from './quoteSheetModel'

/** Cell keys whose formula result should be visually masked while its quote row awaits PO input. */
export function pendingFormulaCells(grid: SheetGrid, roles: Array<SheetRole | null>): Set<string> {
  const header = grid[1] ?? []
  const cells = new Set<string>()
  for (let rowIndex = 2; rowIndex < grid.length; rowIndex += 1) {
    const row = grid[rowIndex] ?? []
    const formulas = new Map<string, string>()
    row.forEach((cell, col) => {
      if (typeof cell === 'string' && cell.startsWith('=')) formulas.set(`${columnLetter(col)}${rowIndex + 1}`, cell.toUpperCase())
    })
    if (formulas.size === 0) continue
    const referenced = new Set<string>()
    for (const formula of formulas.values()) {
      for (const match of formula.toUpperCase().matchAll(/\$?([A-Z]{1,3})\$?(\d+)/g)) {
        referenced.add(`${match[1]}${match[2]}`)
      }
    }
    const rowNumber = rowIndex + 1
    const pendingInputs = new Set<string>()
    row.forEach((cell, col) => {
      const role = roles[col]
      if (role === 'identity' || role === 'compute' || role === 'uom' || role === 'result') return
      const title = typeof header[col] === 'string' ? header[col].trim() : ''
      if (/^(stt|ghi chú|ghi chu|note|notes|mô tả|mo ta)$/i.test(title)) return
      const address = `${columnLetter(col)}${rowNumber}`
      if ((cell === null || cell === '') && referenced.has(address)) pendingInputs.add(address)
    })
    if (pendingInputs.size === 0) continue
    const tainted = new Set<string>()
    let changed = true
    while (changed) {
      changed = false
      for (const [address, formula] of formulas) {
        if (tainted.has(address)) continue
        const refs = [...formula.matchAll(/\$?([A-Z]{1,3})\$?(\d+)/g)].map((match) => `${match[1]}${match[2]}`)
        if (refs.some((ref) => pendingInputs.has(ref) || tainted.has(ref))) {
          tainted.add(address)
          changed = true
        }
      }
    }
    for (const address of tainted) {
      const match = /^([A-Z]+)(\d+)$/.exec(address)
      if (!match) continue
      let column = 0
      for (const letter of match[1]) column = column * 26 + letter.charCodeAt(0) - 64
      cells.add(`${rowIndex}:${column - 1}`)
    }
  }
  return cells
}

export function shouldMaskPendingFormulaCell(cells: ReadonlySet<string>, row: number, column: number): boolean {
  return cells.has(`${row}:${column}`)
}

/** Save role/header and three example rows by default; preserve formulas in retained rows. */
export function templateGrid(grid: SheetGrid, keepAllData: boolean): SheetGrid {
  return keepAllData ? grid.map((row) => [...row]) : grid.slice(0, 5).map((row) => [...row])
}

export function unlistedPoColumnHints(grid: SheetGrid, roles: Array<SheetRole | null>): string[] {
  const headers = grid[1] ?? []
  return headers.flatMap((header, column) => {
    if (typeof header !== 'string' || !header.trim() || roles[column] !== 'info' || guessRoleFromLabel(header) !== 'info') return []
    if (/^(stt|ghi chú|ghi chu|note|notes|mô tả|mo ta|công thức)$/i.test(header.trim())) return []
    return [header.trim()]
  })
}
